import { useState } from 'react';
import type { FootprintResult, VisibilityProbe } from '../types';
import AnalyzedPages from './AnalyzedPages';
import AnswerBlock from './AnswerBlock';

const KNOWS_META: Record<FootprintResult['knowledge']['knows'], { label: string; color: string }> = {
  well: { label: 'knows you', color: '#6BE07A' },
  vaguely: { label: 'vaguely knows you', color: '#E0B341' },
  no: { label: "doesn't know you", color: '#E0574A' },
};

function Foothold({ p, brandTokens, brandDomain }: { p: VisibilityProbe; brandTokens: string[]; brandDomain: string }): JSX.Element {
  const real = p.state === 'mentioned' || p.state === 'cited_only';
  const tone = p.state === 'mentioned' ? '#6BE07A' : p.state === 'cited_only' ? '#E0B341' : '#6B6B76';
  return (
    <div className="border-t border-warm-dark-2 py-2.5">
      <div className="flex items-start gap-2">
        <span className="mt-0.5" style={{ color: tone }}>{real ? '✓' : '·'}</span>
        <div className="flex-1">
          <p className="text-sm text-text-primary">“{p.query}”</p>
          <p className="font-mono text-sm text-text-secondary">
            {real ? (p.state === 'mentioned' ? 'real foothold: mentioned in the answer' : 'real foothold: cited as a source') : 'not confirmed in live search'}
            {' · '}{p.detail}
          </p>
          <AnswerBlock answer={p.answer} citations={p.citations} brandTokens={brandTokens} brandDomain={brandDomain} />
        </div>
      </div>
    </div>
  );
}

export default function FootprintPanel({
  footprint,
  onRecheck,
  rechecking,
  onShowAbout,
}: {
  footprint: FootprintResult;
  onRecheck: (brandName: string) => void;
  rechecking: boolean;
  onShowAbout: () => void;
}): JSX.Element {
  // Runs stored by the background job before it sent the full shape have no
  // `brand` and no `knowledge`. Falling back is what keeps an old run readable
  // instead of blanking the tab.
  const { asked } = footprint;
  const brand = footprint.brand ?? { name: '-', domain: '', aliases: [] };
  const knowledge = footprint.knowledge ?? { knows: 'no' as const, description: null, suggestedQueries: [] };
  const warm = footprint.warm ?? { description: null, suggestedQueries: [] };
  const probes = footprint.probes ?? [];
  const coldMeta = KNOWS_META[knowledge.knows] ?? KNOWS_META.no;
  const realFootholds = probes.filter((p) => p.state !== 'absent').length;
  const [correction, setCorrection] = useState('');
  const [copied, setCopied] = useState(false);

  function copyQuestions(): void {
    void navigator.clipboard.writeText(probes.map((p) => p.query).join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="w-full max-w-2xl mx-auto border border-warm-dark-2 rounded-xl p-5">
      <h2 className="font-serif text-lg mb-1">Where AI already knows you</h2>
      <p className="text-sm text-text-secondary mb-3">
        The model is asked about you two ways: from memory alone (cold), and while reading your page (warm). The gap
        between them is your discoverability problem.
      </p>

      <AnalyzedPages pages={footprint.analyzedPages ?? []} />

      {/* Cold vs warm contrast: the core insight */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div className="border border-warm-dark-2 rounded-lg p-3">
          <p className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Cold: from memory</p>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: coldMeta.color }} />
            <span className="text-sm font-medium" style={{ color: coldMeta.color }}>AI {coldMeta.label}</span>
          </div>
          <p className="text-sm text-text-secondary mt-1">
            {knowledge.knows === 'no'
              ? 'Unprompted, the model has no stored association with your brand.'
              : `Model's memory: “${knowledge.description}”`}
          </p>
        </div>
        <div className="border border-cyan/30 rounded-lg p-3">
          <p className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Warm: reading your page</p>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-cyan" />
            <span className="text-sm font-medium text-cyan">understands you</span>
          </div>
          <p className="text-sm text-text-secondary mt-1">
            {warm.description ? `“${warm.description}”` : 'Reads your page clearly.'}
          </p>
        </div>
      </div>

      {knowledge.knows === 'no' && warm.description && (
        <p className="text-sm text-cyan/80 mb-3">
          → Your page is clear, but AI forgets you unprompted. The gap is your <strong>public footprint</strong>:
          third-party mentions, structured data and directories that tie your name to the problem. Not a content
          problem, and not something worth paying a lot to “guarantee”.{' '}
          <button onClick={onShowAbout} className="text-cyan hover:underline">
            Why, and what your real odds are →
          </button>
        </p>
      )}

      {/* Semi-automatic mode: the approved questions were probed under Visibility.
          Say so plainly rather than showing an empty section. */}
      {probes.length === 0 && footprint.recallOnly && (
        <p className="text-sm text-text-secondary border-t border-warm-dark-2 pt-3 mt-2">
          Your approved questions were searched live under <span className="text-text-primary">Visibility</span>. They
          are not repeated here, so you aren&apos;t charged twice for the same searches. This panel is the part
          Visibility can&apos;t show you: what the model recalls with no page in front of it.
        </p>
      )}

      {/* Verified footholds on the specific queries you own */}
      {probes.length > 0 && (
        <>
          <p className="font-mono text-sm uppercase tracking-widest text-text-secondary mt-2 mb-1">
            Real footholds on the specific problems you own ({realFootholds}/{probes.length})
          </p>
          {probes.map((p, i) => (
            <Foothold key={i} p={p} brandTokens={[brand.name, ...(brand.aliases ?? [])]} brandDomain={brand.domain} />
          ))}
          <p className="font-mono text-sm text-text-secondary mt-2">
            {probes.length} questions built by a two-stage read of your pages (analyse → generate). Each one is
            web-searched live, so the count is capped for cost/time. An admin can raise it.
          </p>

          {/* Turn the questions into on-site content */}
          <div className="border-t border-warm-dark-2 pt-3 mt-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-mono text-sm uppercase tracking-widest text-text-secondary">
                Use these questions on your site
              </span>
              <button
                onClick={copyQuestions}
                className="font-mono text-sm border border-cyan/40 text-cyan rounded px-3 py-1 hover:bg-cyan/10 transition"
              >
                {copied ? 'copied ✓' : 'copy all'}
              </button>
            </div>
            <p className="text-sm text-text-secondary">
              These are the real questions buyers ask an AI where you should appear. Publish clear answers on your site
              (an FAQ / help section with <span className="font-mono">FAQPage</span> schema) so AI can retrieve and cite
              you. That's how you build a real foothold.
            </p>
          </div>
        </>
      )}

      {/* Brand-name correction, always available to users */}
      <div className="mt-3 border-t border-warm-dark-2 pt-3">
        <p className="text-sm text-text-secondary mb-1">
          This checked for <span className="text-text-primary font-mono">{brand.name}</span> ({brand.domain}). Wrong brand
          name? Re-check with the correct one:
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            placeholder="Type the real brand name…"
            className="flex-1 bg-warm-dark border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1.5 text-text-primary placeholder:text-text-secondary outline-none text-sm"
          />
          <button
            onClick={() => correction.trim() && onRecheck(correction.trim())}
            disabled={rechecking || !correction.trim()}
            className="font-mono text-sm border border-cyan/40 text-cyan rounded px-3 disabled:opacity-50 hover:bg-cyan/10 transition"
          >
            {rechecking ? 're-checking…' : 're-check'}
          </button>
        </div>
      </div>

      {/* Exact prompts, admin only (server only returns `asked` for admins) */}
      {asked && (
        <details className="mt-3 border-t border-warm-dark-2 pt-3">
          <summary className="font-mono text-sm text-cyan cursor-pointer">how was this checked? (admin) ↓</summary>
          <div className="mt-2 font-mono text-sm text-text-secondary whitespace-pre-wrap space-y-3">
            <div>
              <div className="text-text-primary">① Cold recall: “does AI know you from memory?”</div>
              <div>system: {asked.coldRecall.system}</div>
              <div>question: {asked.coldRecall.user}</div>
            </div>
            <div>
              <div className="text-text-primary">② Analyse your pages (stage 1)</div>
              <div>system: {asked.analysis.system}</div>
            </div>
            <div>
              <div className="text-text-primary">③ Generate questions from that analysis (stage 2)</div>
              <div>system: {asked.questions.system}</div>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
